import React, { useEffect, useRef } from 'react';
import { trapFocus, focusFirstElement, announceToScreenReader } from '../../utils/accessibility';
import './TrackActionsMenu.css';

/**
 * Props for TrackActionsMenu component
 */
export interface TrackActionsMenuProps {
  /** Track name for display */
  trackName: string;
  /** Whether this is the last track (disable delete if true) */
  isLastTrack: boolean;
  /** Callback when duplicate action is selected */
  onDuplicate: () => void;
  /** Callback when clear action is selected */
  onClear: () => void;
  /** Callback when delete action is selected */
  onDelete: () => void;
  /** Callback when menu is closed */
  onClose: () => void;
}

/**
 * TrackActionsMenu Component
 * 
 * Bottom sheet menu for track-specific actions (Duplicate, Clear, Delete).
 * Slides up from bottom with backdrop overlay.
 * Touch-friendly action buttons with icons.
 */
export const TrackActionsMenu: React.FC<TrackActionsMenuProps> = ({
  trackName,
  isLastTrack,
  onDuplicate,
  onClear,
  onDelete,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Set up focus trap and announce menu opening
   */
  useEffect(() => {
    announceToScreenReader(`${trackName} actions menu opened`);
    
    if (menuRef.current) {
      focusFirstElement(menuRef.current);
      const cleanup = trapFocus(menuRef.current);
      return cleanup;
    }
    
    return undefined;
  }, [trackName]);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  /**
   * Handle action button click
   */
  const handleAction = (action: () => void, actionName: string) => {
    announceToScreenReader(`${actionName} ${trackName}`);
    action();
    onClose();
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="track-actions-menu-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Menu container */}
      <div 
        ref={menuRef}
        className="track-actions-menu" 
        role="dialog" 
        aria-label={`${trackName} actions`}
        aria-modal="true"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="track-actions-menu-header">
          <h3 className="track-actions-menu-title">{trackName}</h3>
          <p className="track-actions-menu-subtitle">Track Actions</p>
        </div>

        {/* Action buttons */}
        <div className="track-actions-menu-actions">
          {/* Duplicate Track */}
          <button
            className="track-action-menu-btn track-action-duplicate"
            onClick={() => handleAction(onDuplicate, 'Duplicating')}
            aria-label={`Duplicate ${trackName}`}
          >
            <div className="track-action-menu-btn-icon">
              <i className="fa-solid fa-copy" aria-hidden="true"></i>
            </div>
            <div className="track-action-menu-btn-content">
              <div className="track-action-menu-btn-title">Duplicate Track</div>
              <div className="track-action-menu-btn-description">
                Create a copy with all notes and settings
              </div>
            </div>
            <div className="track-action-menu-btn-arrow">
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </div>
          </button>

          {/* Clear Pattern */}
          <button
            className="track-action-menu-btn track-action-clear"
            onClick={() => handleAction(onClear, 'Clearing')}
            aria-label={`Clear all notes in ${trackName}`}
          >
            <div className="track-action-menu-btn-icon">
              <i className="fa-solid fa-eraser" aria-hidden="true"></i>
            </div>
            <div className="track-action-menu-btn-content">
              <div className="track-action-menu-btn-title">Clear Pattern</div>
              <div className="track-action-menu-btn-description">
                Remove all notes from this track
              </div>
            </div>
            <div className="track-action-menu-btn-arrow">
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </div>
          </button>

          {/* Delete Track */}
          <button
            className="track-action-menu-btn track-action-delete"
            onClick={() => handleAction(onDelete, 'Deleting')}
            disabled={isLastTrack}
            aria-label={isLastTrack ? 'Cannot delete last track' : `Delete ${trackName}`}
          >
            <div className="track-action-menu-btn-icon">
              <i className="fa-solid fa-trash" aria-hidden="true"></i>
            </div>
            <div className="track-action-menu-btn-content">
              <div className="track-action-menu-btn-title">Delete Track</div>
              <div className="track-action-menu-btn-description">
                {isLastTrack ? 'Cannot delete the last track' : 'Permanently remove this track'}
              </div>
            </div>
            <div className="track-action-menu-btn-arrow">
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </div>
          </button>
        </div>

        {/* Cancel button */}
        <div className="track-actions-menu-footer">
          <button
            className="track-actions-menu-cancel"
            onClick={onClose}
            aria-label="Cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};
